import {
  defaultStageForSignalStatus,
  type SignalCategoryType,
  type SignalStageType,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';

export type UpdateSignalData = {
  signalId: string;
  title?: string;
  stage?: SignalStageType;
  status?: SignalStatusType | null;
  category?: SignalCategoryType;
  endpointId?: string;
  harEntryId?: string;
  entryPointId?: string;
};

const SIGNAL_DATA_FIELDS: ReadonlyArray<keyof UpdateSignalData> = [
  'status',
  'category',
  'endpointId',
  'harEntryId',
  'entryPointId',
];

export const getMethodColor = (
  method?: string,
): 'red' | 'orange' | 'blue' | 'green' | 'purple' | 'gray' => {
  if (!method) return 'gray';
  const m = method.toUpperCase();
  if (m === 'GET') return 'blue';
  if (m === 'POST') return 'green';
  if (m === 'PUT') return 'orange';
  if (m === 'DELETE') return 'red';
  if (m === 'PATCH') return 'purple';
  return 'gray';
};

const hasStagedFieldValue = (
  field: keyof UpdateSignalData,
  editedData: UpdateSignalData,
): boolean => {
  const value = editedData[field];
  if (value === undefined) return false;
  if (value === '') return false;
  return true;
};

export const isFieldModified = (
  field: keyof UpdateSignalData,
  baselineData: UpdateSignalData,
  editedData: UpdateSignalData,
): boolean => {
  if (!hasStagedFieldValue(field, editedData)) {
    return false;
  }
  return editedData[field] !== baselineData[field];
};

export const hasChanges = (
  editedData: UpdateSignalData,
  baselineData: UpdateSignalData,
): boolean =>
  SIGNAL_DATA_FIELDS.some((field) =>
    isFieldModified(field, baselineData, editedData),
  );

export const buildUpdateSignalSubmitPayload = ({
  baselineData,
  editedData,
  isEndpointEditable,
}: {
  baselineData: UpdateSignalData;
  editedData: UpdateSignalData;
  isEndpointEditable: boolean;
}): UpdateSignalData => {
  const payload: UpdateSignalData = { signalId: editedData.signalId };

  if (isFieldModified('status', baselineData, editedData)) {
    payload.status = editedData.status;
    payload.stage = defaultStageForSignalStatus({
      status: editedData.status ?? null,
    });
  }
  if (isFieldModified('category', baselineData, editedData)) {
    payload.category = editedData.category;
  }
  if (
    isEndpointEditable &&
    isFieldModified('endpointId', baselineData, editedData)
  ) {
    payload.endpointId = editedData.endpointId;
  }
  if (
    isEndpointEditable &&
    isFieldModified('harEntryId', baselineData, editedData)
  ) {
    payload.harEntryId = editedData.harEntryId;
  }
  if (
    isEndpointEditable &&
    isFieldModified('entryPointId', baselineData, editedData)
  ) {
    payload.entryPointId = editedData.entryPointId;
  }

  return payload;
};

export const hasSubmitChanges = ({
  baselineData,
  editedData,
  isEndpointEditable,
}: {
  baselineData: UpdateSignalData;
  editedData: UpdateSignalData;
  isEndpointEditable: boolean;
}): boolean => {
  const payload = buildUpdateSignalSubmitPayload({
    baselineData,
    editedData,
    isEndpointEditable,
  });
  const { signalId: _signalId, ...updates } = payload;
  return Object.keys(updates).length > 0;
};

export const getModifiedCount = (
  baselineData: UpdateSignalData,
  editedData: UpdateSignalData,
): number =>
  SIGNAL_DATA_FIELDS.filter((field) =>
    isFieldModified(field, baselineData, editedData),
  ).length;

export const omitEndpointUpdateFields = (
  data: UpdateSignalData,
): UpdateSignalData => {
  const {
    endpointId: _endpointId,
    harEntryId: _harEntryId,
    entryPointId: _entryPointId,
    ...rest
  } = data;
  return rest;
};

export const clearUpdateSignalEdits = (
  data: UpdateSignalData,
): UpdateSignalData => ({
  signalId: data.signalId,
  ...(data.title !== undefined ? { title: data.title } : {}),
});
